import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {GlobalService} from 'app/global/global.service';
import {catchError, map, mergeMap, of, switchMap, tap} from 'rxjs';
import {DashboardService} from '../dashboard.service';
import * as DashboardActions from '../store/dashboard.action';
import {DocumentDTO} from './dashboard.dto';

@Injectable({
    providedIn: 'root'
})

export class DashboardEffects {
    constructor (
        private _action$: Actions,
        private _dashboardService$: DashboardService,
        private _globalService$: GlobalService
    ) { }



    fetchLastSignedDocuments$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchLastSignedDocuments),
            switchMap(() => {
                return this._dashboardService$.lastSignedDocuments().pipe(
                    map((response: DocumentDTO[]) => DashboardActions.fetchLastSignedDocumentsSucceeded(response)),
                    catchError(error => of(DashboardActions.fetchLastSignedDocumentsFailed(error)))
                )
            })
        )
    });





    fetchEmailBouncedDocument$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchEmailBouncedDocuments),
            switchMap(() => {
                return this._dashboardService$.emailBouncedDocuments().pipe(
                    map((response: DocumentDTO[]) => DashboardActions.fetchEmailBouncedDocumentsSucceeded(response)),
                    catchError(error => of(DashboardActions.fetchEmailBouncedDocumentsFailed(error)))
                )
            })
        )
    });




    fetchExpireDocuments$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchExpireContracts),
            switchMap(() => {
                return this._dashboardService$.expireContracts().pipe(
                    map((response) => DashboardActions.fetchExpireContractsSucceeded(response)),
                    catchError(error => of(DashboardActions.fetchExpireContractsFailed(error)))
                )
            })
        )
    });




    fetchMostActiveUsers$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.mostActiveUsers),
            switchMap(() => {
                return this._dashboardService$.mostActiveUsers().pipe(
                    map(response => DashboardActions.mostActiveUsersSucceeded(response)),
                    catchError(error => of(DashboardActions.mostActiveUsersFailed(error)))
                )
            })
        )
    });

    dashboardSettings$ = createEffect(() => {
      return this._action$.pipe(
        ofType(DashboardActions.fetchDashboardSettings),
        switchMap(() => {
          return this._dashboardService$.getSettings().pipe(
            map((response) => {
              const settingsObj: {[key:string] : string } = {};
              const defaultWidegts = ['UploadDocument','LatestActivity','TaskReminders'];
              for (let i = 0; i < response.length; i++) {
                const j = defaultWidegts.indexOf(response[i].key);
                if(j > -1){
                    defaultWidegts.splice(j,1);
                }
                settingsObj[response[i].key] = response[i].value;
              }

              for(let i = 0; i < defaultWidegts.length; i ++)
              {
                settingsObj[defaultWidegts[i]] = 'active';
              }

              return DashboardActions.fetchDashboardSettingsSucceeded(settingsObj); // Dispatching action with payload
            }),
            catchError(error => of(DashboardActions.fetchDashboardSettingsFailed(error)))
          );
        })
      );
    });

    updateDashboardSetting$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.updateDashboardSetting),
            mergeMap((params) => {
                return this._dashboardService$.updateSetting(params.key, params.value).pipe(
                    map(() => DashboardActions.updateDashboardSettingSucceeded(params.key, params.value)),
                    catchError(error => of(error))
                )
            })
        )
    });

    fetchLatestActivity$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchLatestActivity),
            switchMap(() => {
                return this._dashboardService$.latestActivity().pipe(
                    map(response => DashboardActions.fetchLatestActivitySucceeded(response)),
                    catchError(error => of(DashboardActions.fetchLatestActivityFailed(error)))
                )
            })
        )
    });

    fetchMostUsedTemplates$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchMostUsedTemplates),
            switchMap(() => {
                return this._dashboardService$.mostUsedTemplates().pipe(
                    map(response => DashboardActions.fetchMostUsedTemplatesSucceeded(response)),
                    catchError(error => of(DashboardActions.fetchMostUsedTemplatesFailed(error)))
                )
            })
        )
    });

    fetchAboutToExpire$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchAboutToExpire),
            switchMap(() => {
                return this._dashboardService$.aboutToExpire().pipe(
                    map(response => DashboardActions.fetchAboutToExpireSucceeded(response)),
                    catchError(error => of(DashboardActions.fetchAboutToExpireFailed(error)))
                )
            })
        )
    });

    fetchUnreadChatMessages$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchUnreadChatMessages),
            switchMap(() => {
                return this._dashboardService$.unreadChatMessages().pipe(
                    map(response => DashboardActions.fetchUnreadChatMessagesSucceeded(response)),
                    catchError(error => of(DashboardActions.fetchUnreadChatMessagesFailed(error)))
                )
            })
        )
    });

    fetchForgottenDocuments$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchForgottenDocuments),
            switchMap(() => {
                return this._dashboardService$.forgottenDocuments().pipe(
                    map(response => DashboardActions.fetchForgottenDocumentsSucceeded(response)),
                    catchError(error => of(DashboardActions.fetchForgottenDocumentsFailed(error)))
                )
            })
        )
    });

    fetchAutoRenewalOn$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchAutoRenewalOn),
            switchMap(() => {
                return this._dashboardService$.autoRenewalOn().pipe(
                    map(response => DashboardActions.fetchAutoRenewalOnSucceeded(response)),
                    catchError(error => of(DashboardActions.fetchAutoRenewalOnFailed(error)))
                )
            })
        )
    });



    fetchTaskAndReminderSettings$ = createEffect(() => {
        return this._action$.pipe(
            ofType(DashboardActions.fetchTaskAndReminders),
            switchMap(() => {
                return this._dashboardService$.fetchTaskAndReminderSettings().pipe(
                    map(response => DashboardActions.fetchTaskAndRemindersSucceeded(response)),
                    catchError(error => of(DashboardActions.fetchTaskAndRemindersFailed(error)))
                )
            })
        )
    });



    extractErrorFromAction$ = createEffect(() => {
        return this._action$.pipe(
            ofType(
                DashboardActions.fetchLastSignedDocumentsFailed,
                DashboardActions.fetchEmailBouncedDocumentsFailed,
                DashboardActions.fetchExpireContractsFailed,
                DashboardActions.mostActiveUsersFailed,
                DashboardActions.fetchDashboardSettingsFailed,
                DashboardActions.fetchLatestActivityFailed,
                DashboardActions.fetchMostUsedTemplatesFailed,
                DashboardActions.fetchAboutToExpireFailed,
                DashboardActions.fetchUnreadChatMessagesFailed,
                DashboardActions.fetchForgottenDocumentsFailed,
                DashboardActions.fetchAutoRenewalOnFailed,
                DashboardActions.fetchTaskAndRemindersFailed,
            ),
            tap((args) => {
                this._globalService$.toastrError(args.error);
            })
        )
    }, {dispatch: false})
    
}
